import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Keyboard, Platform } from 'react-native';
import Animated from 'react-native-reanimated';
import { CommonActions } from '@react-navigation/native';
import { useAppSelector, useAppStore } from 'store/hooks';
import { AppView, EditDeleteModal, SettingsSection, Spacer } from 'design-system';
import BlurAmountView from 'design-system/BlurAmountView';
import EmojiPickerModal from 'design-system/emojiPicker/EmojiPickerModal';
import EmojiText from 'design-system/EmojiText';
import withAnimatedNavigationHeader from 'design-system/hoc/withAnimatedNavigationHeader';
import { IconArrow, IconDocument, IconEdit, IconGoal } from 'design-system/icons';
import IconArchive from 'design-system/icons/IconArchive';
import IconBoldQuestion from 'design-system/icons/IconBoldQuestion';
import IconInfo from 'design-system/icons/IconInfo';
import IconPieChart from 'design-system/icons/IconPieChart';
import { IconSaveMoney } from 'design-system/icons/IconSaveMoney';
import IconUnarchive from 'design-system/icons/IconUnarchive';
import NavigationHeaderRightIconButton from 'design-system/NavigationHeaderRightIconButton';
import SectionHeader from 'design-system/SectionHeader';
import { SettingsItem, SettingsItemContent, SettingsItemDescription, SettingsItemNavigate, SettingsItemRightContent, SettingsItemTitle, } from 'design-system/SettingsItem';
import StatusBar from 'design-system/StatusBar';
import Text from 'design-system/Text';
import { SCROLL_INDICATOR_INSETS } from 'design-system/values';
import { muteAllButThrows, useFetchOld } from 'hooks';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import useColors from 'hooks/useColors';
import { useMarginBottom } from 'hooks/useMarginBottom';
import useRequiredBenefitCallback from 'features/premium/hooks/useRequiredBenefitCallback';
import { formatAmount, formatPlural } from 'utils/formatting';
import Links, { knownLinks } from 'utils/links';
import Modal from 'utils/packages/Modal';
import { deletePot, reopenPot, updatePot } from '../actions';
import FSCSCard from '../components/FSCSCard';
import { selectPotProductFromProductId } from '../reducers/selectors';
import { SingleInputScreenFlows } from '../types';
export const PotSettingsScreen = withAnimatedNavigationHeader(({ scrollHandler, navigation, route, navigation: { pop, navigateDeprecated } }) => {
    const pot = route.params?.pot;
    const colors = useColors();
    const isMounted = useRef(true);
    const scrambled = useAppSelector((store) => store.utils.scrambled);
    const autosaveDistribution = useAppSelector((store) => store.pots.savingsPlan?.distribution);
    const totalAllocationItems = useAppSelector((store) => store.pots.savingsPlan?.totalAllocationItems);
    const interestProduct = useAppSelector((store) => selectPotProductFromProductId(store, pot.productId));
    const store = useAppStore();
    const isInterestPot = pot.type === 'INTEREST' || pot.type === 'INTEREST_NOTICE';
    const isAccumulatedInterestPot = pot.type === 'ACCUMULATED_INTEREST' || pot.type === 'ACCUMULATED_INTEREST_NOTICE';
    const [isEmojiPickerOpen, setIsEmojiPickerOpen] = useState(false);
    const [, , , fetch] = useFetchOld(muteAllButThrows);
    useEffect(() => () => {
        isMounted.current = false;
    }, []);
    useLayoutEffect(() => {
        const onArchivePress = () => {
            const hasPending = store
                .getState()
                .unpersistedPotsReducer.autoSaveActivities?.find((activity) => activity.status === 'PENDING' &&
                activity.savingsPlanInfo.destinations.find((dest) => dest.potId === pot.id) !== undefined) !== undefined;
            if (hasPending) {
                Modal.showConfirmation({
                    title: 'You can’t archive this pot yet',
                    subtitle: 'There’s a pending transaction on this pot. Once it’s completed, you’ll be able to archive it.',
                    confirmButtonTitle: 'Okay',
                    onConfirm: Modal.hide,
                });
            }
            else if (autosaveDistribution && autosaveDistribution[pot.id]) {
                Modal.showConfirmation({
                    title: 'You have an active Autosave',
                    subtitle: 'This pot is currently part of your Autosave allocation. To archive it, please update your allocation first.',
                    confirmButtonTitle: 'Yes, edit my allocation',
                    cancelButtonTitle: 'No, thanks',
                    onConfirm: () => navigation.navigateDeprecated('AutosaveDistribution', { isEditFlow: true }),
                });
            }
            else if (pot.balance?.value > 0) {
                Modal.showConfirmation({
                    title: 'Archive this pot?',
                    subtitle: 'This pot still has a balance. You’ll need to withdraw the money before you can archive it. Once empty, you can close it anytime.',
                    confirmButtonTitle: 'Okay',
                    onConfirm: () => Modal.hide(),
                });
            }
            else {
                Modal.showConfirmation({
                    title: 'Archive this pot?',
                    subtitle: 'Are you sure you want to archive this pot? You can unarchive it anytime.',
                    confirmButtonTitle: 'Yes, let me archive',
                    cancelButtonTitle: 'No, thanks',
                    onConfirm: async () => {
                        try {
                            pop(2);
                            await fetch(deletePot(pot.id));
                        }
                        catch {
                            // nothing to do here, api middleware shows the error on its own
                        }
                    },
                });
            }
        };
        const handleUnarchive = () => Modal.showConfirmation({
            title: 'Unarchive this pot?',
            subtitle: 'It will return to your active pots.',
            confirmButtonTitle: 'No, thanks',
            cancelButtonTitle: 'Yes, unarchive',
            onPressCancel: async () => {
                try {
                    pop(3);
                    await fetch(reopenPot(pot.id));
                }
                catch {
                    // nothing to do here, api middleware shows the error on its own
                }
            },
        });
        navigation.setOptions({
            headerRight: () => isAccumulatedInterestPot ? null : (<NavigationHeaderRightIconButton onPress={pot.deletedAt ? handleUnarchive : onArchivePress}>
              {pot.deletedAt ? (<IconUnarchive color={colors.buttons.primary}/>) : (<IconArchive color={colors.buttons.primary}/>)}
            </NavigationHeaderRightIconButton>),
        });
    }, [
        autosaveDistribution,
        colors.buttons.primary,
        isAccumulatedInterestPot,
        pot.deletedAt,
        pot.id,
        pot?.balance?.value,
    ]);
    const onEditNamePress = useCallback(() => {
        navigateDeprecated('SingleInput', {
            pot,
            defaultValue: pot.name,
            flow: SingleInputScreenFlows.editPotName,
        });
    }, [pot]);
    const onEmojiModalClose = useCallback(() => {
        setIsEmojiPickerOpen(false);
    }, []);
    const onSelectEmoji = useCallback(async (selectedEmoji) => {
        onEmojiModalClose();
        if (pot.emoji !== selectedEmoji) {
            await fetch(updatePot({ ...pot, emoji: selectedEmoji }));
            const potScreen = navigation.getState().routes.find((route) => route.name === 'Pot');
            const potSettingsScreen = navigation.getState().routes.find((route) => route.name === 'PotSettings');
            if (potScreen) {
                navigation.dispatch({
                    ...CommonActions.setParams({
                        screenParams: {
                            ...pot,
                            emoji: selectedEmoji,
                        },
                    }),
                    source: potScreen.key,
                });
            }
            if (potSettingsScreen) {
                navigation.dispatch({
                    ...CommonActions.setParams({
                        pot: {
                            ...pot,
                            emoji: selectedEmoji,
                        },
                    }),
                    source: potSettingsScreen.key,
                });
            }
        }
    }, [onEmojiModalClose, pot]);
    const onEditEmojiPress = useCallback(() => {
        Keyboard.dismiss();
        EmojiPickerModal.show(pot.emoji, onSelectEmoji, onEmojiModalClose);
        setIsEmojiPickerOpen(true);
    }, [onEmojiModalClose, onSelectEmoji, pot.emoji]);
    const handleEditGoal = useRequiredBenefitCallback(useCallback(() => {
        navigateDeprecated('EditPotGoal', { pot });
    }, [pot]), 'savingGoals');
    const handleGoalPress = useCallback(() => {
        if (pot.goal) {
            EditDeleteModal.show({
                onEdit: handleEditGoal,
                onDelete: async () => {
                    try {
                        await fetch(updatePot({ ...pot, goal: undefined }));
                        const firstScreen = navigation.getState().routes[1];
                        const secondScreen = navigation.getState().routes[2];
                        if (firstScreen.name === 'Pot') {
                            navigation.dispatch({
                                ...CommonActions.setParams({
                                    screenParams: {
                                        ...pot,
                                        goal: undefined,
                                    },
                                }),
                                source: firstScreen.key,
                            });
                        }
                        if (secondScreen.name === 'PotSettings') {
                            navigation.dispatch({
                                ...CommonActions.setParams({
                                    pot: {
                                        ...pot,
                                        goal: undefined,
                                    },
                                }),
                                source: secondScreen.key,
                            });
                        }
                    }
                    catch {
                        // no need to show anything, the error middleware already did
                    }
                },
            });
            return;
        }
        handleEditGoal();
    }, [handleEditGoal, pot]);
    const handleAllocation = useCallback(() => {
        navigation.navigateDeprecated('AutosaveDistribution', { isEditFlow: true });
    }, []);
    const disabled = !!pot.deletedAt;
    const renderHeader = useMemo(() => {
        if (isAccumulatedInterestPot) {
            return null;
        }
        return (<SettingsSection>
          <SettingsItemNavigate title={pot.name} iconSize={44} disabled={disabled} description="Pot name" onPress={onEditNamePress} icon={<IconEdit color={colors.elements.brand}/>}/>
          <SettingsItemNavigate title="Emoji" iconSize={44} disabled={disabled} onPress={onEditEmojiPress} isSelected={isEmojiPickerOpen} icon={<EmojiText fontSize={20}>{pot.emoji}</EmojiText>}/>
          <SettingsItem iconSize={44} disabled={disabled} icon={<IconGoal gradientKey="ultimate"/>} onPress={handleGoalPress}>
            <SettingsItemContent>
              <SettingsItemDescription>Goal</SettingsItemDescription>
              <BlurAmountView enabled={scrambled}>
                <SettingsItemTitle>
                  {pot.goal ? (`${formatAmount(Number(pot.goal.amount), pot.balance.currency, 2)} `) : (<Text Text-16 Secondary>
                      Set a goal
                    </Text>)}
                </SettingsItemTitle>
              </BlurAmountView>
            </SettingsItemContent>
            {!disabled && (<SettingsItemRightContent>
                <IconArrow />
              </SettingsItemRightContent>)}
          </SettingsItem>
          {!!(autosaveDistribution && autosaveDistribution[pot.id] && totalAllocationItems) && (<SettingsItemNavigate iconSize={44} description="Allocation" onPress={handleAllocation} icon={<IconPieChart gradientKey="yellow"/>} title={totalAllocationItems > 1
                    ? `This pot & ${totalAllocationItems - 1} ${formatPlural('other', 'others', totalAllocationItems - 1)}`
                    : 'This pot'}/>)}
          <SettingsItem icon={<IconSaveMoney gradientKey="pro"/>} iconSize={44}>
            <SettingsItemContent>
              <SettingsItemDescription>Pot type</SettingsItemDescription>
              <SettingsItemTitle>{interestProduct?.name}</SettingsItemTitle>
            </SettingsItemContent>
          </SettingsItem>
        </SettingsSection>);
    }, [
        isAccumulatedInterestPot,
        pot.name,
        pot.emoji,
        pot.goal,
        pot.balance.currency,
        pot.id,
        disabled,
        onEditNamePress,
        isEmojiPickerOpen,
        colors.elements.brand,
        onEditEmojiPress,
        handleGoalPress,
        scrambled,
        autosaveDistribution,
        totalAllocationItems,
        handleAllocation,
        interestProduct?.name,
    ]);
    const handleRateInfo = useCallback(() => {
        navigation.navigateDeprecated('ReviewAndConfirm', { productId: pot.productId });
    }, [pot.productId]);
    const handleTerms = useCallback(() => {
        if (Platform.OS === 'android') {
            navigateDeprecated('Pdf', { url: knownLinks.potsTnc, buttonTitle: 'Done' });
        }
        else {
            Links.openPotsTnc();
        }
    }, []);
    const handleTrusteeTnc = useCallback(() => {
        if (interestProduct?.termsAndConditions) {
            if (Platform.OS === 'android') {
                navigation.navigateDeprecated('Pdf', { url: interestProduct.termsAndConditions });
            }
            else {
                Links.openLink(interestProduct.termsAndConditions, true);
            }
        }
    }, [interestProduct?.termsAndConditions]);
    const renderInterestInfo = useMemo(() => (<>
          <Spacer h={24}/>
          <SectionHeader horizontalMargins>Documents</SectionHeader>
          <Spacer h={16}/>
          <SettingsSection noMarginTop noMarginBottom>
            {isInterestPot || isAccumulatedInterestPot ? (<SettingsItemNavigate iconSize={44} title="Rate information" onPress={handleRateInfo} icon={<IconInfo gradientKey="deepPink"/>}/>) : null}
            <SettingsItemNavigate iconSize={44} title="Emma Pots terms" onPress={handleTerms} icon={<IconDocument alt gradientKey="darkPurple"/>}/>
            {isInterestPot || isAccumulatedInterestPot ? (<SettingsItemNavigate iconSize={44} onPress={handleTrusteeTnc} title="Trustee Terms & Conditions" icon={<IconBoldQuestion color={colors.gradients.skyBlue.colors[0]}/>}/>) : null}
          </SettingsSection>
        </>), [
        colors.gradients.skyBlue.colors,
        handleRateInfo,
        handleTerms,
        handleTrusteeTnc,
        isAccumulatedInterestPot,
        isInterestPot,
    ]);
    const { paddingHorizontalStyle } = useAppFrameDimensions();
    const contentContainerStyle = useMarginBottom('paddingBottom', undefined, paddingHorizontalStyle);
    return (<AppView withFrame={false}>
        <StatusBar animated barStyle="dark-content"/>
        <Animated.ScrollView onScroll={scrollHandler} scrollEventThrottle={16} scrollIndicatorInsets={SCROLL_INDICATOR_INSETS} contentContainerStyle={contentContainerStyle}>
          {renderHeader}
          {renderInterestInfo}
          {isInterestPot || isAccumulatedInterestPot ? <FSCSCard productId={pot.productId}/> : null}
        </Animated.ScrollView>
      </AppView>);
}, ({ colors }) => ({
    title: 'Settings',
    headerColorOutputRange: [colors.background.dark, colors.cards.onDark],
}));
export default PotSettingsScreen;
